import { memo, useRef } from 'react';

import { Slider } from '../../../Slider/Slider';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpAssets } from '../../../../modules/assets';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { usePlaceholders } from '../../usePlaceholders';
import { useComponentAssets } from '../../../utils/useComponentAssets';
import { Story } from '../../../Story/Story';
import { useViewObserver } from '../../../utils/useViewObserver';
import { isTouchDevice } from '../../../../utils/device';

import { useIntersection } from './useIntersection';
import { useTracking } from './useTracking';

import type { StorySliderComponent } from '@vgtv/api-client/lib/component/slider';
import type { FC } from 'react';

import * as styles from './StoriesSlider.module.scss';

interface StoriesSliderProps {
  componentConfig: StorySliderComponent;
  pageComponentIndex: number;
}

const StoriesSliderBase: FC<StoriesSliderProps> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { id, header } = componentConfig;

  const ref = useRef<HTMLDivElement>(null!);
  const { isFetched } = useComponentDataFetch({
    componentConfig,
    fetchAction: fetchSvpAssets,
  });

  const { currentIndex, handleIntersectionChange } = useIntersection();
  const { trackSliderView, trackStoryClick } = useTracking({
    elementType: 'StoriesSlider',
    pageComponentIndex,
    header,
  });

  useViewObserver({
    onView: trackSliderView,
    elementRef: ref,
  });

  const assets = useComponentAssets(id);

  const placeholders = usePlaceholders(isFetched);

  if (isFetched && assets.length === 0) {
    return null;
  }

  return (
    <Slider header={header} className={styles.slider} ref={ref}>
      {placeholders.map((_, index) => (
        <Placeholder key={index} type={placeholderTypes.story} />
      ))}
      {assets.map((asset, index) => (
        <Story
          asset={asset}
          key={asset.id}
          index={index}
          onIntersectionChange={handleIntersectionChange}
          isPreviewEnabled={isTouchDevice && index === currentIndex}
          onClick={() => trackStoryClick(asset, index)}
        />
      ))}
    </Slider>
  );
};

export const StoriesSlider = memo(StoriesSliderBase);
